import { useEffect, useRef, useState } from "react";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import {
  Divider,
  Flex,
  Input,
  isNullish,
  List,
  Menu,
  Text,
  ToolIcon
} from "@theorgchartdev/storybook";
import { GetFilteredTypedFieldsList } from "./ImportFilterModal";
import { getHoverTipContent } from "@/Utils/Misc";
import { appstr } from "@coreapp/utils/appstr";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import {
  enumObjectTypes,
  kExcludedLimitFieldsEmployeeCharts,
  kExcludedLimitFieldsPositionCharts
} from "@coreapp/constants";
import { IMPORT_FILTERS_TEST_PREFIX } from "../ImportFiltersUtils";

export const GetLimitFields = (importFilter, chartConfig) => {
  let limitFields = importFilter["limitfields"];
  if (!limitFields) {
    limitFields = GetFilteredTypedFieldsList(chartConfig);
  } else if (Array.isArray(limitFields)) {
    // Legacy Limit Fields
    return {
      [enumObjectTypes.holder]: limitFields,
      [enumObjectTypes.position]: []
    };
  }

  return limitFields;
};

const holderSeparator = {
  content: (
    <Text key="separator-employees" modifier="bold">
      {appstr("Employee")}
    </Text>
  ),
  enableHoverBackground: false,
  icon: "users",
  value: "holder-separator"
};
const positionSeparator = {
  content: (
    <Text key="separator" modifier="bold">
      {appstr("Position")}
    </Text>
  ),
  enableHoverBackground: false,
  value: "position-separator",
  icon: "cubes"
};

const BuildFieldOptions = (importFilter, chartConfig, onClick) => {
  const limitFields = GetLimitFields(importFilter, chartConfig);

  const typedFields = GetFilteredTypedFieldsList(chartConfig);
  const positionFields = typedFields[enumObjectTypes.position] ?? [];
  const holderFields = typedFields[enumObjectTypes.holder] ?? [];
  const isPositionChart = IsPositionChart(chartConfig);
  const hiddenSystemFields = isPositionChart
    ? kExcludedLimitFieldsPositionCharts
    : kExcludedLimitFieldsEmployeeCharts;

  const positionOptions = positionFields
    .filter(field => {
      return (
        limitFields[enumObjectTypes.position].indexOf(field) === -1 &&
        hiddenSystemFields[enumObjectTypes.position].indexOf(field) === -1
      );
    })
    .map(field => ({
      clickHandler: onClick,
      label: field,
      value: field,
      key: `position-${field}`,
      type: enumObjectTypes.position
    }));

  const holderOptions = holderFields
    .filter(field => {
      return (
        limitFields[enumObjectTypes.holder].indexOf(field) === -1 &&
        hiddenSystemFields[enumObjectTypes.holder].indexOf(field) === -1
      );
    })
    .map(field => ({
      clickHandler: onClick,
      label: field,
      value: field,
      key: `holder-${field}`,
      type: enumObjectTypes.holder
    }));

  if (isPositionChart) {
    return [
      {
        label: appstr("Employee fields"),
        value: "employeeFields",
        keepOpenOnClick: true,
        options: holderOptions,
        disabled: holderOptions.length === 0,
        listProps: {
          enableSearch: true
        }
      },
      {
        label: appstr("Position fields"),
        value: "positionFields",
        keepOpenOnClick: true,
        options: positionOptions,
        disabled: positionOptions.length === 0,
        listProps: {
          enableSearch: true
        }
      }
    ];
  } else {
    return holderOptions;
  }
};

const BuildActiveFieldOptions = (searchQuery, importFilter, chartConfig, onRemove) => {
  const limitFields = GetLimitFields(importFilter, chartConfig);
  const isPositionChart = IsPositionChart(chartConfig);
  const excludedFields = isPositionChart
    ? kExcludedLimitFieldsPositionCharts
    : kExcludedLimitFieldsEmployeeCharts;

  let fieldsCopy = structuredClone(limitFields);

  if (searchQuery.length > 0) {
    const filterFunction = field => {
      if (isNullish(field)) {
        return false;
      }

      return field.toLowerCase().lastIndexOf(searchQuery.toLowerCase()) !== -1;
    };
    fieldsCopy[enumObjectTypes.holder] = fieldsCopy[enumObjectTypes.holder].filter(filterFunction);
    fieldsCopy[enumObjectTypes.position] =
      fieldsCopy[enumObjectTypes.position].filter(filterFunction);
  }

  const optionBuilder = (field, type, index) => ({
    content: (
      <ActionListItem
        label={field}
        actions={[
          {
            tooltipProps: getHoverTipContent("limitfield"),
            iconName: "trash-can",
            iconProps: { color: "error" },
            onClick: () => onRemove(field, type),
            dataTestId: `${IMPORT_FILTERS_TEST_PREFIX}-remove-limit-field-${type}-${index}`
          }
        ]}
      />
    ),
    value: field,
    key: `${type}-${field}`
  });

  const holderFields = fieldsCopy[enumObjectTypes.holder]
    .filter(field => {
      return excludedFields[enumObjectTypes.holder].indexOf(field) === -1;
    })
    .map((field, i) => optionBuilder(field, enumObjectTypes.holder, i));
  const positionFields = fieldsCopy[enumObjectTypes.position]
    .filter(field => {
      return excludedFields[enumObjectTypes.position].indexOf(field) === -1;
    })
    .map((field, i) => optionBuilder(field, enumObjectTypes.position, i));
  const addFieldTypeSeparators = IsPositionChart(chartConfig);

  const options = [];

  if (addFieldTypeSeparators && holderFields.length > 0) options.push(holderSeparator);
  options.push(...holderFields);
  if (addFieldTypeSeparators && positionFields.length > 0) options.push(positionSeparator);
  options.push(...positionFields);

  return options;
};

export const ImportFilterLimitFieldsInput = props => {
  const [searchQuery, setSearchQuery] = useState("");
  const fieldMenuRef = useRef(null);

  const fieldList = GetFilteredTypedFieldsList(props.chartConfig);
  const limitFields = GetLimitFields(props.importFilter, props.chartConfig);

  const disableReset =
    limitFields[enumObjectTypes.holder].length === fieldList[enumObjectTypes.holder].length &&
    limitFields[enumObjectTypes.position].length === fieldList[enumObjectTypes.position].length;

  const handleReset = () => {
    setSearchQuery("");
    const nextFilter = structuredClone(props.importFilter);
    nextFilter["limitfields"] = GetFilteredTypedFieldsList(props.chartConfig);
    props.setImportFilter(nextFilter);
  };

  const handleAddLimitField = ({ value, type }) => {
    fieldMenuRef.current?.setOpen(false);
    const nextFilter = structuredClone(props.importFilter);
    nextFilter["limitfields"] = GetLimitFields(nextFilter, props.chartConfig);
    nextFilter["limitfields"][type].push(value);
    props.setImportFilter(nextFilter);
  };

  const handleRemoveLimitField = (value, type) => {
    const nextFilter = structuredClone(props.importFilter);
    const targetIndex = nextFilter["limitfields"][type].indexOf(value);
    nextFilter["limitfields"] = GetLimitFields(nextFilter, props.chartConfig);
    nextFilter["limitfields"][type].splice(targetIndex, 1);
    props.setImportFilter(nextFilter);
  };

  const activeFieldOptions = BuildActiveFieldOptions(
    searchQuery,
    props.importFilter,
    props.chartConfig,
    handleRemoveLimitField
  );

  return (
    <>
      <Divider />
      <Flex direction="row" justifyContent="space-between">
        <Text size="medium">{appstr("Limit Fields")}</Text>
        <Flex gap="8px">
          <ToolIcon
            name={"rotate-left"}
            size="medium-small"
            onClick={handleReset}
            disabled={disableReset}
            tooltipProps={getHoverTipContent("ResetLimitFields")}
            data-testid={`${props.dataTestIdPrefix}-reset-button`}
          />
          <Menu
            openCtrlRef={fieldMenuRef}
            listWidth={"large"}
            popoverProps={{
              flipOptions: {
                mainAxis: false
              }
            }}
            trigger={
              <ToolIcon
                name={"plus-solid"}
                size="medium-small"
                disabled={disableReset}
                tooltipProps={getHoverTipContent("AddFieldValuePickerFilter")}
                data-testid={`${props.dataTestIdPrefix}-add-button`}
              />
            }
            listProps={{
              enableSearch: !IsPositionChart(props.chartConfig)
            }}
            options={BuildFieldOptions(props.importFilter, props.chartConfig, handleAddLimitField)}
            dataTestId={`${props.dataTestIdPrefix}-field-menu`}
          />
        </Flex>
      </Flex>
      <Input
        inputOnly
        size="small"
        placeholder={appstr("Find")}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        data-testid={`${props.dataTestIdPrefix}-find`}
      />
      {activeFieldOptions.length > 0 ? (
        <Flex height="30vh">
          <List listWidth="fill" optionSize={"small"} options={activeFieldOptions} />
        </Flex>
      ) : (
        <Text colorModifier={"lightgrey"}>
          {searchQuery.length > 0 ? appstr("nomatches") : appstr("All fields restricted")}
        </Text>
      )}
    </>
  );
};
